import { useState, useEffect, useRef } from 'react'
import { useAppSelector } from '../../store/hooks'
import { X, ChevronDown } from 'lucide-react'

interface PatientOption {
  id: string
  name: string
  species: string
  owner_name: string
}

interface NewTreatmentModalProps {
  open: boolean
  onClose: () => void
  onCreated: (treatment: { id: string }) => void
}

const treatmentTypeOptions = [
  'Wellness Exam',
  'Sick Visit',
  'Surgery',
  'Dental',
  'Vaccination',
  'Emergency',
  'Follow-Up',
] as const

export function NewTreatmentModal({ open, onClose, onCreated }: NewTreatmentModalProps) {
  const veterinarians = useAppSelector((s) => s.settings.veterinarians)

  const [patientId, setPatientId] = useState('')
  const [patientSearch, setPatientSearch] = useState('')
  const [patients, setPatients] = useState<PatientOption[]>([])
  const [showPatientDropdown, setShowPatientDropdown] = useState(false)

  const [treatmentType, setTreatmentType] = useState('')
  const [showTypeDropdown, setShowTypeDropdown] = useState(false)

  const [diagnosis, setDiagnosis] = useState('')

  const [veterinarianId, setVeterinarianId] = useState('')
  const [showVetDropdown, setShowVetDropdown] = useState(false)

  const [treatmentDate, setTreatmentDate] = useState('')
  const [notes, setNotes] = useState('')
  const [errors, setErrors] = useState<string[]>([])
  const [saving, setSaving] = useState(false)

  const patientDropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (open) {
      setPatientId('')
      setPatientSearch('')
      setPatients([])
      setShowPatientDropdown(false)
      setTreatmentType('')
      setShowTypeDropdown(false)
      setDiagnosis('')
      setVeterinarianId('')
      setShowVetDropdown(false)
      setTreatmentDate(new Date().toISOString().slice(0, 10))
      setNotes('')
      setErrors([])
      setSaving(false)
    }
  }, [open])

  // Search patients
  useEffect(() => {
    if (!open) return
    const timeout = setTimeout(async () => {
      try {
        const searchParam = patientSearch.trim()
          ? `?search=${encodeURIComponent(patientSearch)}`
          : ''
        const res = await fetch(
          `/.netlify/functions/controlled-substances-patients${searchParam}`
        )
        if (res.ok) {
          const data = await res.json()
          setPatients(data)
          if (patientSearch.trim()) {
            setShowPatientDropdown(true)
          }
        }
      } catch {
        // ignore
      }
    }, 200)
    return () => clearTimeout(timeout)
  }, [patientSearch, open])

  // Close patient dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (patientDropdownRef.current && !patientDropdownRef.current.contains(e.target as Node)) {
        setShowPatientDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const handleSelectPatient = (patient: PatientOption) => {
    setPatientId(patient.id)
    setPatientSearch(patient.name)
    setShowPatientDropdown(false)
  }

  const validate = (): string[] => {
    const errs: string[] = []
    if (!patientId) errs.push('Patient is required')
    if (!treatmentType) errs.push('Treatment Type is required')
    return errs
  }

  const handleSave = async () => {
    const errs = validate()
    if (errs.length > 0) {
      setErrors(errs)
      return
    }
    setErrors([])
    setSaving(true)

    try {
      const res = await fetch('/.netlify/functions/treatments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          patient_id: patientId,
          treatment_type: treatmentType,
          diagnosis,
          veterinarian_id: veterinarianId || null,
          treatment_date: treatmentDate || null,
          clinical_notes: notes,
          status: 'Planned',
        }),
      })
      if (!res.ok) throw new Error('Failed to create treatment')
      const created = await res.json()
      onCreated(created)
    } catch {
      setErrors(['Failed to create treatment. Please try again.'])
    } finally {
      setSaving(false)
    }
  }

  if (!open) return null

  const selectedVetName = veterinarianId
    ? veterinarians.find((v) => v.id === veterinarianId)?.name || ''
    : ''

  return (
    <div
      data-testid="new-treatment-modal-overlay"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        data-testid="new-treatment-modal"
        className="bg-surface rounded-[8px] w-full max-w-lg max-h-[85vh] overflow-y-auto"
        style={{ boxShadow: 'var(--shadow-elevation-2)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <h2 className="text-[14px] font-semibold text-text-primary">New Treatment</h2>
          <button
            data-testid="new-treatment-modal-close"
            onClick={onClose}
            className="flex items-center justify-center w-7 h-7 rounded-[4px] text-text-muted hover:bg-hover transition-colors duration-100 cursor-pointer"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex flex-col gap-3 px-5 py-4">
          {/* Patient (searchable dropdown) */}
          <div className="flex flex-col gap-1" ref={patientDropdownRef}>
            <label className="text-[11px] text-text-muted">
              Patient <span className="text-status-error">*</span>
            </label>
            <div className="relative">
              <input
                data-testid="new-treatment-patient"
                type="text"
                value={patientSearch}
                onChange={(e) => {
                  setPatientSearch(e.target.value)
                  if (!e.target.value.trim()) {
                    setPatientId('')
                  }
                }}
                onFocus={() => {
                  if (patients.length > 0) setShowPatientDropdown(true)
                }}
                placeholder="Search patient..."
                className="w-full h-7 px-2 rounded-[4px] border border-border bg-base text-[13px] text-text-primary outline-none focus:ring-2 focus:ring-accent/40"
              />
              {showPatientDropdown && patients.length > 0 && (
                <div
                  data-testid="new-treatment-patient-dropdown"
                  className="absolute z-10 top-full left-0 w-full mt-1 bg-surface border border-border rounded-[4px] max-h-40 overflow-y-auto"
                  style={{ boxShadow: 'var(--shadow-elevation-1)' }}
                >
                  {patients.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      data-testid={`patient-option-${p.id}`}
                      onClick={() => handleSelectPatient(p)}
                      className="w-full text-left px-2 py-1.5 text-[13px] text-text-primary hover:bg-hover cursor-pointer"
                    >
                      {p.name}
                      <span className="text-[11px] text-text-muted ml-1">
                        ({p.species} - {p.owner_name})
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Treatment Type */}
          <div className="flex flex-col gap-1">
            <label className="text-[11px] text-text-muted">
              Treatment Type <span className="text-status-error">*</span>
            </label>
            <div className="relative">
              <button
                type="button"
                data-testid="new-treatment-type"
                onClick={() => {
                  setShowTypeDropdown(!showTypeDropdown)
                  setShowVetDropdown(false)
                }}
                className="w-full h-7 px-2 rounded-[4px] border border-border bg-base text-[13px] text-left text-text-primary outline-none focus:ring-2 focus:ring-accent/40 cursor-pointer flex items-center justify-between"
              >
                <span>{treatmentType || 'Select treatment type...'}</span>
                <ChevronDown size={12} className="text-text-muted" />
              </button>
              {showTypeDropdown && (
                <div
                  data-testid="new-treatment-type-dropdown"
                  className="absolute z-10 top-full left-0 w-full mt-1 bg-surface border border-border rounded-[4px] max-h-40 overflow-y-auto"
                  style={{ boxShadow: 'var(--shadow-elevation-1)' }}
                >
                  {treatmentTypeOptions.map((option) => (
                    <button
                      key={option}
                      type="button"
                      data-testid={`treatment-type-option-${option}`}
                      onClick={() => {
                        setTreatmentType(option)
                        setShowTypeDropdown(false)
                      }}
                      className="w-full text-left px-2 py-1.5 text-[13px] text-text-primary hover:bg-hover cursor-pointer"
                    >
                      {option}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Diagnosis */}
          <div className="flex flex-col gap-1">
            <label className="text-[11px] text-text-muted">Diagnosis</label>
            <input
              data-testid="new-treatment-diagnosis"
              type="text"
              value={diagnosis}
              onChange={(e) => setDiagnosis(e.target.value)}
              placeholder="Enter diagnosis..."
              className="w-full h-7 px-2 rounded-[4px] border border-border bg-base text-[13px] text-text-primary outline-none focus:ring-2 focus:ring-accent/40"
            />
          </div>

          {/* Assigned Veterinarian */}
          <div className="flex flex-col gap-1">
            <label className="text-[11px] text-text-muted">Assigned Veterinarian</label>
            <div className="relative">
              <button
                type="button"
                data-testid="new-treatment-vet"
                onClick={() => {
                  setShowVetDropdown(!showVetDropdown)
                  setShowTypeDropdown(false)
                }}
                className="w-full h-7 px-2 rounded-[4px] border border-border bg-base text-[13px] text-left text-text-primary outline-none focus:ring-2 focus:ring-accent/40 cursor-pointer flex items-center justify-between"
              >
                <span>{selectedVetName || 'Select veterinarian...'}</span>
                <ChevronDown size={12} className="text-text-muted" />
              </button>
              {showVetDropdown && (
                <div
                  data-testid="new-treatment-vet-dropdown"
                  className="absolute z-10 top-full left-0 w-full mt-1 bg-surface border border-border rounded-[4px] max-h-40 overflow-y-auto"
                  style={{ boxShadow: 'var(--shadow-elevation-1)' }}
                >
                  {veterinarians.map((vet) => (
                    <button
                      key={vet.id}
                      type="button"
                      data-testid={`vet-option-${vet.id}`}
                      onClick={() => {
                        setVeterinarianId(vet.id)
                        setShowVetDropdown(false)
                      }}
                      className="w-full text-left px-2 py-1.5 text-[13px] text-text-primary hover:bg-hover cursor-pointer"
                    >
                      {vet.name}
                    </button>
                  ))}
                  {veterinarians.length === 0 && (
                    <p className="px-2 py-1.5 text-[12px] text-text-muted">
                      No veterinarians configured
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Date */}
          <div className="flex flex-col gap-1">
            <label className="text-[11px] text-text-muted">Date</label>
            <input
              data-testid="new-treatment-date"
              type="date"
              value={treatmentDate}
              onChange={(e) => setTreatmentDate(e.target.value)}
              className="w-full h-7 px-2 rounded-[4px] border border-border bg-base text-[13px] text-text-primary outline-none focus:ring-2 focus:ring-accent/40"
            />
          </div>

          {/* Notes */}
          <div className="flex flex-col gap-1">
            <label className="text-[11px] text-text-muted">Notes</label>
            <textarea
              data-testid="new-treatment-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional clinical notes..."
              rows={3}
              className="px-2 py-1 rounded-[4px] border border-border bg-base text-[13px] text-text-primary outline-none focus:ring-2 focus:ring-accent/40 resize-none"
            />
          </div>

          {/* Errors */}
          {errors.length > 0 && (
            <div data-testid="new-treatment-errors" className="text-[11px] text-status-error">
              {errors.map((err, i) => (
                <p key={i}>{err}</p>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border">
          <button
            data-testid="new-treatment-cancel"
            onClick={onClose}
            className="inline-flex items-center h-7 px-3 rounded-[4px] text-[13px] text-text-secondary hover:bg-hover transition-colors duration-100 cursor-pointer"
          >
            Cancel
          </button>
          <button
            data-testid="new-treatment-save"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center h-7 px-3 rounded-[4px] bg-accent text-white text-[13px] hover:bg-accent-dark transition-colors duration-100 cursor-pointer disabled:opacity-50 disabled:cursor-default"
          >
            {saving ? 'Creating...' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  )
}
